import { Injectable, signal } from '@angular/core';
import { catchError, forkJoin, of } from 'rxjs';
import { ApiRepository } from '../../infrastructure/api.repository';

export interface EnumValueItem {
  value: number;
  name: string;
  displayName: string;
}

export interface ContractItem {
  contractId: number;
  contractName: string | null;
}

export interface PropertyRegistryItem {
  propertyRegistry: string;
  value: string;
}

export interface IneProvinceItem {
  ineCodProv: number;
  name: string | null;
}

export interface IneMunicipalityItem {
  ineCodMun: number;
  name: string | null;
}

@Injectable({ providedIn: 'root' })
export class EnumCacheService extends ApiRepository {
  private readonly loaded = signal(false);
  private readonly municipalitiesCache = new Map<number, IneMunicipalityItem[]>();

  readonly appraisalCompanies = signal<EnumValueItem[]>([]);
  readonly appraisalPurposes = signal<EnumValueItem[]>([]);
  readonly propertyRegistries = signal<PropertyRegistryItem[]>([]);
  readonly ineProvinces = signal<IneProvinceItem[]>([]);
  readonly ineMunicipalities = signal<IneMunicipalityItem[]>([]);
  readonly contracts = signal<ContractItem[]>([]);

  ensureLoaded(): void {
    if (this.loaded()) return;
    this.loaded.set(true);

    forkJoin({
      companies: this.get<EnumValueItem[]>('/enums/spanishAppraisalCompanies').pipe(catchError(() => of([] as EnumValueItem[]))),
      purposes: this.get<EnumValueItem[]>('/enums/AppraisalPurposes').pipe(catchError(() => of([] as EnumValueItem[]))),
      registries: this.get<PropertyRegistryItem[]>('/enums/propertyRegistries').pipe(catchError(() => of([] as PropertyRegistryItem[]))),
      provinces: this.get<IneProvinceItem[]>('/enums/Provinces').pipe(catchError(() => of([] as IneProvinceItem[]))),
      contracts: this.get<ContractItem[]>('/entityConnections/GetAll').pipe(catchError(() => of([] as ContractItem[]))),
    }).subscribe(({ companies, purposes, registries, provinces, contracts }) => {
      this.appraisalCompanies.set(companies);
      this.appraisalPurposes.set(purposes);
      this.propertyRegistries.set(registries);
      this.ineProvinces.set(provinces);
      this.contracts.set(contracts);
    });
  }

  getContractLabel(contractId: number | null | undefined): string {
    if (contractId == null) return '—';
    const found = this.contracts().find(c => c.contractId === contractId);
    return found?.contractName ?? String(contractId);
  }

  loadMunicipalitiesForProvince(ineCodProv: number): void {
    if (this.municipalitiesCache.has(ineCodProv)) {
      this.ineMunicipalities.set(this.municipalitiesCache.get(ineCodProv)!);
      return;
    }
    this.get<IneMunicipalityItem[]>(`/enums/Municipalities/${ineCodProv}`)
      .pipe(catchError(() => of([] as IneMunicipalityItem[])))
      .subscribe(items => {
        this.municipalitiesCache.set(ineCodProv, items);
        this.ineMunicipalities.set(items);
      });
  }

  getAppraisalCompanyLabel(value: string | null | undefined): string {
    if (!value) return '—';
    const found = this.appraisalCompanies().find(c => c.value === Number(value));
    return found?.displayName ?? found?.name ?? '—';
  }

  getAppraisalPurposeLabel(value: string | null | undefined): string {
    if (!value) return '—';
    const found = this.appraisalPurposes().find(p => p.value === Number(value));
    return found?.displayName ?? found?.name ?? '—';
  }
}
